import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import SingleMediaFileRoute from './Routes/SingleMediaFileRoute'
import MultipleMediaFileRoute from './Routes/MultipleMediaFileRoute'
import StoryAdRoute from './Routes/StoryAdRoute'

import EventSourceStep from './Steps/EventSourceStep'
import AdCreateRouteSelector from './Steps/AdCreateRouteSelector'
import ExistingPostRoute from './Routes/ExistingPostRoute'

import useLineItemDetails from './hooks/useLineItemDetails'
import useStepFormRoutes from '../../../../../hooks/formHooks/useStepFormRoutes'
import useIsInternalAdCreation from '../../../../../features/hooks/internalAdHooks/useIsInternalAdCreation'

import { formatOptionsList } from '../../../../../features/formatters'
import { getSelectedPlatform } from '../../../../../helpers/handlePlatformSelection'

import { clearCreateAd, setAdCarouselSelectedIndex } from '../../../../../modules/actions/ads'
import {
  clearChoicesCTAByGoal,
  clearChoicesPixels,
  getChoicesCTAByGoal,
  getChoicesPixels
} from '../../../../../modules/actions/choices'

import { choicesCTAByGoalWasLoadedSelector, choicesPixelsSelector } from '../../../../../modules/selectors/choices'
import { selectedFacebookAdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import {
  adCreateErrorSelector,
  adCreateLoadingSelector,
  adWasCreatedSelector
} from '../../../../../modules/selectors/ads'
import { accessibleSocialPagesIsLoadingSelector } from '../../../../../modules/selectors/socialAccounts'

import { AD_FACEBOOK_CREATE } from '../../../../../constants/forms'
import { FORMAT_SINGLE, FORMAT_CAROUSEL, FORMAT_STORY, FORMAT_EXISTING_POST } from '../../fields'
import { AdFacebookCreateContext } from '../../../formsContexts/AdFacebookCreateFormContextProvider'

const AdFacebookCreateForm = ({
  formProgressItems,
  onSuccessSubmit,
  lineItemWasLoaded,
  selectedApplicationId,
  selectedPixelPassedFromOutside,
  allowSendPixel = true
}) => {
  const dispatch = useDispatch()

  const selectedPlatform = getSelectedPlatform()
  const pixels = useSelector(choicesPixelsSelector)
  const adWasCreated = useSelector(adWasCreatedSelector)

  const { campaignObjective } = useContext(AdFacebookCreateContext)

  const ctaWasLoaded = useSelector(choicesCTAByGoalWasLoadedSelector(campaignObjective))

  const facebookPagesIsLoading = useSelector(accessibleSocialPagesIsLoadingSelector)

  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  const { showAsStory: isStoryRoute } = useLineItemDetails()

  const isInternalAdCreation = useIsInternalAdCreation()

  const manageStepFormRoutes = useStepFormRoutes({ initialRoute: FORMAT_SINGLE })

  const formattedPixels = useMemo(
    () =>
      formatOptionsList({
        list: pixels,
        labelName: 'name',
        valueName: 'id'
      }),
    [pixels]
  )

  const getSelectedRoute = useCallback(
    props => {
      switch (manageStepFormRoutes.selectedRoute) {
        case FORMAT_EXISTING_POST:
          return <ExistingPostRoute {...props} />
        case FORMAT_CAROUSEL:
          return <MultipleMediaFileRoute {...props} />
        case FORMAT_STORY:
          return <StoryAdRoute {...props} />
        case FORMAT_SINGLE:
        default:
          return <SingleMediaFileRoute {...props} />
      }
    },
    [manageStepFormRoutes.selectedRoute]
  )

  const clearCreateAdHandler = useCallback(() => {
    dispatch(clearCreateAd())
  }, [dispatch])

  useEffect(() => {
    dispatch(getChoicesPixels({ account: selectedAdAccountId }, selectedPlatform))
  }, [dispatch, selectedAdAccountId, selectedPlatform])

  useEffect(() => {
    if (campaignObjective && !ctaWasLoaded) {
      dispatch(getChoicesCTAByGoal({ goals: campaignObjective }))
    }
  }, [dispatch, campaignObjective, ctaWasLoaded])

  useEffect(() => {
    if (lineItemWasLoaded && isStoryRoute) {
      manageStepFormRoutes.handleSelectRoute(FORMAT_STORY)
    }
  }, [manageStepFormRoutes, lineItemWasLoaded, isStoryRoute])

  useEffect(() => {
    return () => {
      dispatch(setAdCarouselSelectedIndex('adCreateForm', 0))
      dispatch(clearChoicesPixels())
      dispatch(clearChoicesCTAByGoal())
    }
  }, [dispatch])

  const showEventSourceStep = useMemo(() => pixels.length > 1 && allowSendPixel, [pixels, allowSendPixel])

  return getSelectedRoute({
    formName: AD_FACEBOOK_CREATE,
    successSubmit: adWasCreated,
    errorSelector: adCreateErrorSelector,
    isLoadingSelector: adCreateLoadingSelector,
    // this allow to clearCreateAd on error
    clearSubmitHandler: clearCreateAdHandler,
    eventSourceStep: {
      component: <EventSourceStep formattedPixels={formattedPixels} />,
      show: showEventSourceStep
    },
    selectedApplicationId,
    selectedPixelPassedFromOutside,
    allowSendPixel,
    // FormRouteSelector component is adding in each Route as a first step and allows to control Route Selecting
    FormRouteSelector: <AdCreateRouteSelector manageStepFormRoutes={manageStepFormRoutes} />,
    isInitialDataLoading: !lineItemWasLoaded || facebookPagesIsLoading,
    isInternalAdCreation,
    onSuccessSubmit,
    formProgressItems
  })
}

AdFacebookCreateForm.propTypes = {
  formProgressItems: PropTypes.array,
  onSuccessSubmit: PropTypes.func,
  // some of the props controlled outside of the form
  // it helps to make it more dynamic, like using on rights summary page with multi-form approach
  campaignObjective: PropTypes.string,
  lineItemWasLoaded: PropTypes.bool.isRequired,
  selectedApplicationId: PropTypes.string,
  selectedPixelPassedFromOutside: PropTypes.string,
  allowSendPixel: PropTypes.bool
}

export default AdFacebookCreateForm
